import type {
  AmmTelemetryView,
  CandleSeries,
  InstrumentTelemetryView,
  Percent,
  Quantity,
  Rate,
  TradeView,
} from '@nutrimatic/sdk'

import type { MarketCandle, RecentTrade } from '@/data/marketApi/types'

export function shortNutrimaticAddr(addr: string | undefined | null, chars = 4): string {
  if (!addr) return '—'
  if (addr.length <= chars * 2 + 2) return addr
  return `${addr.slice(0, chars + 2)}…${addr.slice(-chars)}`
}

export function formatNutrimaticQuantity(q: Quantity | undefined | null): string {
  if (!q) return '—'
  try {
    const raw = typeof q.raw === 'bigint' ? q.raw : BigInt(q.raw)
    const decimals = q.decimals
    if (decimals <= 0) return raw.toString()
    const neg = raw < 0n
    const abs = neg ? -raw : raw
    const base = 10n ** BigInt(decimals)
    const whole = abs / base
    const frac = abs % base
    if (frac === 0n) return `${neg ? '-' : ''}${whole.toString()}`
    const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '').slice(0, 6)
    return `${neg ? '-' : ''}${whole.toString()}.${fracStr}`
  } catch {
    return String(q.raw)
  }
}

function clampInt(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n))
}

function trimTrailingZeros(s: string): string {
  if (!s.includes('.')) return s
  return s.replace(/\.?0+$/, '')
}

/** Decimals for |n| < 1 so small unit prices stay visible (never collapse to $0.00). */
function fractionalUsdDecimals(abs: number): number {
  if (!(abs > 0) || !Number.isFinite(abs)) return 2
  return clampInt(Math.ceil(-Math.log10(abs)) + 3, 2, 8)
}

function formatUsdCompact(n: number): string | null {
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(2)}K`
  return null
}

/**
 * Shared USD formatter.
 * - `price`: unit token price — adaptive decimals below $1; 4 (trim) at/above $1.
 * - `amount`: TVL / volume / mcap — 2 decimals for $1–$999; adaptive below $1.
 */
function formatUsdNumber(n: number, kind: 'price' | 'amount'): string {
  if (n === 0) return '$0'
  const compact = formatUsdCompact(n)
  if (compact) return compact

  const abs = Math.abs(n)
  if (abs >= 1) {
    if (kind === 'amount') return `$${n.toFixed(2)}`
    return `$${trimTrailingZeros(n.toFixed(4))}`
  }

  const decimals = fractionalUsdDecimals(abs)
  let body = trimTrailingZeros(n.toFixed(decimals))
  // Guard: never emit $0 for a non-zero value after rounding.
  if (body === '0' || body === '-0') {
    body = trimTrailingZeros(n.toFixed(8))
  }
  return `$${body}`
}

/** Aggregates: TVL, volume, mcap, liquidity. */
export function formatNutrimaticUsd(n: number | undefined | null): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return formatUsdNumber(n, 'amount')
}

/** Unit token price in USD — honest display for sub-cent assets. */
export function formatNutrimaticUsdPrice(n: number | undefined | null): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return formatUsdNumber(n, 'price')
}

export function formatNutrimaticPrice(rate: Rate | null | undefined): string {
  if (!rate) return '—'
  const v = typeof rate.value === 'number' ? rate.value : Number(rate.value)
  if (!Number.isFinite(v)) return String(rate.value)
  if (v >= 1000) return v.toFixed(2)
  if (v >= 1) return v.toFixed(4)
  return v.toPrecision(4)
}

export function formatNutrimaticChange(change: Percent | null | undefined): string {
  if (change == null || !Number.isFinite(change.value)) return '—'
  // Contract: Percent.value is already a percent (2.3 → +2.3%).
  const pct = change.value
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}

export function formatPoolRowMetrics(pool: AmmTelemetryView | null | undefined): {
  price: string
  change24h: string
  volume24h: string
  tvl: string
  tvlUsd: number
} {
  return {
    price: formatNutrimaticPrice(pool?.spot),
    change24h: formatNutrimaticChange(pool?.change24h),
    volume24h: formatNutrimaticUsd(pool?.volume24hUsd),
    tvl: formatNutrimaticUsd(pool?.tvlUsd),
    tvlUsd: pool?.tvlUsd ?? 0,
  }
}

export function formatInstrumentPrice(view: InstrumentTelemetryView | null | undefined): {
  priceUsd: string
  change24h: string
  volume24h: string
  liquidityUsd: string
} {
  return {
    priceUsd:
      view?.priceUsd != null && Number.isFinite(view.priceUsd)
        ? formatNutrimaticUsdPrice(view.priceUsd)
        : '—',
    change24h: formatNutrimaticChange(view?.change24h),
    volume24h: formatNutrimaticUsd(view?.market?.volume24hUsd),
    liquidityUsd: formatNutrimaticUsd(view?.market?.liquidityUsd),
  }
}

export function candleSeriesToMarketCandles(series: CandleSeries | null | undefined): MarketCandle[] {
  if (!series) return []
  const byTime = new Map<number, MarketCandle>()
  for (const c of series.candles) {
    byTime.set(c.time, {
      time: c.time,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    })
  }
  if (series.partialCandle) {
    const p = series.partialCandle
    byTime.set(p.time, {
      time: p.time,
      open: p.open,
      high: p.high,
      low: p.low,
      close: p.close,
    })
  }
  return [...byTime.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, c]) => c)
}

export function tradeViewsToRecent(events: readonly TradeView[] | undefined): RecentTrade[] {
  if (!events?.length) return []
  return [...events]
    .slice()
    .reverse()
    .map((t) => ({
      id: t.eventId,
      price: String(t.price),
      amount: String(t.baseAmount),
      side: t.side,
      time: new Date(t.time * 1000).toISOString(),
      txHash: t.txHash,
    }))
}
